// Beyond-Use-Date calculator: a conservative use-by date for a reconstituted
// peptide from its reconstitution date + storage condition. Pure date math is
// exported for tests; DOM wiring is guarded. Not medical advice.

// --- Pure math (unit-tested in tests/bud-calc-math.test.mjs) ------------------

// Conservative windows. Fridge = USP-797 28-day multi-dose (preserved) headline.
// Room temp = use same day. Freezer = single-use aliquots, no refreeze.
export const BUD_DAYS = { fridge: 28, room: 1, freezer: 90 };

export function addDays(date, days) {
  const d = new Date(date.getTime());
  d.setDate(d.getDate() + days);
  return d;
}

export function useByDate(reconDate, storage) {
  const days = BUD_DAYS[storage];
  if (days === undefined) return null;
  return addDays(reconDate, days);
}

export function daysBetween(a, b) {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

// --- DOM wiring (browser only) -----------------------------------------------
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

function boot() {
  const root = document.querySelector('.is-bud');
  if (!root) return;
  const $ = (s) => root.querySelector(s);
  const $$ = (s) => root.querySelectorAll(s);
  const dateEl = $('#is-bud-date');
  const empty = $('#is-bud-result-empty');
  const ok = $('#is-bud-result-ok');
  const headlineEl = $('#is-bud-headline');
  const sublineEl = $('#is-bud-subline');
  const guidanceEl = $('#is-bud-guidance');

  const GUIDANCE = {
    fridge: 'Store at 2-8 degrees C (fridge, not the door). This 28-day window follows USP-797 for a preserved multi-dose vial - many peptides are stable longer, but 28 days is the safe default. Discard if the solution turns cloudy or shows particles.',
    room: 'Reconstituted peptide left at room temperature degrades quickly. Draw your dose and return the vial to the fridge the same day - do not store it out on the bench.',
    freezer: 'Only freeze single-use aliquots at -20 degrees C, and never refreeze a thawed aliquot (ice crystals damage the peptide). This is an approximate long-term window - confirm stability for your specific peptide.',
  };

  const state = { date: null, storage: 'fridge' };

  function fmt(d) {
    return d.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }

  function recompute() {
    if (!state.date || !state.storage) {
      empty.hidden = false; ok.hidden = true; writeUrl(); return;
    }
    const recon = new Date(state.date + 'T00:00:00');
    if (isNaN(recon.getTime())) { empty.hidden = false; ok.hidden = true; return; }
    const useBy = useByDate(recon, state.storage);
    if (!useBy) { empty.hidden = false; ok.hidden = true; return; }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const left = daysBetween(today, useBy);
    headlineEl.textContent = 'Use by ' + fmt(useBy);
    if (left > 1) sublineEl.textContent = BUD_DAYS[state.storage] + '-day window - about ' + left + ' days left from today.';
    else if (left === 1) sublineEl.textContent = BUD_DAYS[state.storage] + '-day window - use it tomorrow at the latest.';
    else if (left === 0) sublineEl.textContent = BUD_DAYS[state.storage] + '-day window - today is the last day.';
    else sublineEl.textContent = BUD_DAYS[state.storage] + '-day window - this passed ' + Math.abs(left) + ' days ago; discard it.';
    guidanceEl.textContent = GUIDANCE[state.storage] || '';
    empty.hidden = true; ok.hidden = false;
    writeUrl();
  }

  if (dateEl) {
    dateEl.addEventListener('change', () => { state.date = dateEl.value || null; recompute(); });
  }
  $$('[data-storage]').forEach((btn) => {
    btn.addEventListener('click', () => {
      state.storage = btn.dataset.storage;
      $$('[data-storage]').forEach((b) => {
        const active = b === btn;
        b.classList.toggle('is-calc__pill--active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      recompute();
    });
  });

  // --- Shareable URL state ---
  function writeUrl() {
    if (!window.history || !window.history.replaceState) return;
    const params = new URLSearchParams();
    if (state.date) params.set('date', state.date);
    if (state.storage && state.storage !== 'fridge') params.set('storage', state.storage);
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }
  function readUrlPrefill() {
    const p = new URLSearchParams(window.location.search);
    const st = p.get('storage');
    if (st === 'room' || st === 'freezer' || st === 'fridge') {
      const btn = root.querySelector('[data-storage="' + st + '"]');
      if (btn) btn.click();
    }
    const dt = p.get('date');
    if (dt && /^\d{4}-\d{2}-\d{2}$/.test(dt) && dateEl) {
      dateEl.value = dt; state.date = dt; recompute();
    }
  }
  readUrlPrefill();
}
